import { useCallback, useEffect, useRef, useState } from 'react'
import { restoreRunsApi } from './api'

/**
 * The sign-in pass for the restore daemon (the bridge document, revised 13 Oct 2026, section 2).
 *
 * Orchestrate's back end signs a short pass for the signed-in engineer; this fetches it when the
 * page opens, keeps it in memory only, and fetches a new one before it runs out - the daemon
 * cannot ask Orchestrate anything, so a pass simply stops working when it expires. The token is
 * passed on exactly as received.
 */
export const RENEW_BEFORE_MS = 5 * 60 * 1000
const RETRY_MS = 30 * 1000

export interface PassProblem { code: string | null; message: string }

export function useSessionPass() {
    const [pass, setPass] = useState<{ token: string; notAfter: string } | null>(null)
    const [problem, setProblem] = useState<PassProblem | null>(null)
    const alive = useRef(true)
    useEffect(() => () => { alive.current = false }, [])

    const renew = useCallback(async () => {
        try {
            const { token, notAfter } = (await restoreRunsApi.sessionPass()).data.data
            if (!alive.current) return
            setPass({ token, notAfter })
            setProblem(null)
        } catch (error) {
            if (!alive.current) return
            const body = (error as { response?: { data?: { code?: string; message?: string; error?: string } } })?.response?.data
            setProblem({ code: body?.code ?? null, message: body?.message || body?.error || (error as Error).message })
        }
    }, [])

    useEffect(() => { void renew() }, [renew])
    // A new pass shortly before this one expires. A pass that could not be fetched is tried again,
    // unless the server says signing is not set up - asking again will not change that.
    useEffect(() => {
        let wait: number | null = null
        if (pass) wait = Math.max(new Date(pass.notAfter).getTime() - Date.now() - RENEW_BEFORE_MS, 10 * 1000)
        else if (problem && problem.code !== 'not_set_up') wait = RETRY_MS
        if (wait == null) return undefined
        const timer = setTimeout(() => { void renew() }, wait)
        return () => clearTimeout(timer)
    }, [pass, problem, renew])

    // An expired pass is not offered at all.
    const live = pass && new Date(pass.notAfter).getTime() > Date.now() ? pass : null
    return { token: live?.token ?? '', notAfter: live?.notAfter ?? null, problem, renew }
}
